import React, { useEffect } from "react";
import { AiOutlineMenu } from "react-icons/ai";
import { MdKeyboardArrowDown } from "react-icons/md";
import CryptoJS from "crypto-js";
import { UserProfile } from ".";
import { useStateContext } from "../contexts/ContextProvider";
import useAuth from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import getCustomerByAuth from "../graphql/queries/GetCustomerByAuth";
import { FiUser } from "react-icons/fi";

const NavButton = ({ title, customFunc, icon, color, dotColor }) => (
  <button
    type="button"
    onClick={() => customFunc()}
    style={{ color }}
    className="relative text-xl rounded-full p-3 hover:bg-light-gray"
  >
    <span
      style={{ background: dotColor }}
      className="absolute inline-flex rounded-full h-2 w-2 right-2 top-2"
    />
    {icon}
  </button>
);

const Navbar = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { loading, error, data } = useQuery(getCustomerByAuth, {
    variables: { id: userId },
  });
  const customer = data?.customers_by_pk;

  const {
    currentColor,
    activeMenu,
    setActiveMenu,
    handleClick,
    isClicked,
    setScreenSize,
    screenSize,
  } = useStateContext();

  useEffect(() => {
    if (userId === null) {
      navigate("/login");
    }
  }, [userId, navigate]);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [setScreenSize]);

  useEffect(() => {
    if (screenSize <= 900) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  }, [screenSize, setActiveMenu]);

  const handleActiveMenu = () => setActiveMenu(!activeMenu);

  // Decrypt function
  async function decryptMessage(encryptedData) {
    const key = CryptoJS.enc.Utf8.parse(
      "AXRATECH" +
        customer?.customer_provider?.encrypt_key +
        customer?.customer_provider?.sms_reference_id
    );

    const iv = CryptoJS.enc.Utf8.parse(
      customer?.customer_provider?.encrypt_key.padEnd(16, "0")
    );
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error("Decryption failed:", error);
      return null;
    }
  }

  // Fetch and decrypt data
  useEffect(() => {
    async function addAuth() {
      try {
        const decryptedText = await decryptMessage(
          customer?.encrypted_password
        );
        const response = await fetch(
          `https://api.axpigeon.axra.app/rest/api/customers/auth`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: customer?.name,
              password: decryptedText,
            }),
          }
        );
        const { data } = await response.json();
        window.localStorage.setItem(`axpigeon_customer`, data?.accessToken);
      } catch (error) {
        console.error("Error during decryption:", error);
      }
    }

    if (customer) {
      addAuth();
    }
  }, [customer]);

  return (
    <div className="flex justify-between p-2 md:ml-6 md:mr-6 relative">
      <NavButton
        title="Menu"
        customFunc={handleActiveMenu}
        color={currentColor}
        icon={<AiOutlineMenu />}
      />
      <div className="flex">
        <div
          className="flex items-center gap-2 cursor-pointer p-1 hover:bg-light-gray dark:hover:bg-secondary-dark-bg rounded-lg group"
          onClick={() => handleClick("userProfile")}
        >
          <div className="p-2 rounded-full border">
            <FiUser className="w-5 h-5 group-hover:dark:text-white dark:text-white text-gray-400" />
          </div>
          <p>
            <span className="text-gray-400 text-14">Hi,</span>{" "}
            <span className="text-gray-400 font-bold ml-1 text-14">
              {customer?.username}
            </span>
          </p>
          <MdKeyboardArrowDown className="text-gray-400 text-14" />
        </div>
        {isClicked.userProfile && <UserProfile userData={customer} />}
      </div>
    </div>
  );
};

export default Navbar;
